import { useState, useEffect, useMemo } from 'react';

import moment from 'moment';

import useForm from '../utils/useForm';
import VendorService from '../../services/VendorService';
import LoanService from '../../services/LoanService';
import { formatCurrency } from '../../helpers/currencyMask';
import PropertyService from '../../services/PropertyService';

const vendorTypeOptions = [
  {
    value: 'Tax',
    label: 'Tax',
  },
  {
    value: 'Insurance',
    label: 'Insurance',
  },
  {
    value: 'HOA',
    label: 'HOA',
  },
  {
    value: 'Distribution',
    label: 'Distribution',
  },
];

const lenderTypeOptions = [
  {
    value: 'Advance Repayment',
    label: 'Advance Repayment',
  },
  {
    value: 'Distribution',
    label: 'Distribution',
  },
];

const servicerTypeOptions = [
  {
    value: 'Servicer Fees',
    label: 'Servicer Fees',
  },
];

const borrowerTypeOptions = [
  {
    value: 'Refund',
    label: 'Refund',
  },
  {
    value: 'Impound Refund',
    label: 'Impound Refund',
  },
  {
    value: 'Claim Distribution',
    label: 'Claim Distribution',
  },
];

// This form is used with updated /bill endpoint
const useNewBillForm = (
  onSubmit,
  values,
  loanId,
  shouldReset,
  borrowers,
  propertyId
) => {
  const [typeOptions, setTypeOptions] = useState(vendorTypeOptions);
  const [vendorType, setVendorType] = useState('');
  const [isBillPayed, setIsBillPayed] = useState(false);

  const isVendor = typeOptions === vendorTypeOptions;
  const isServicer = typeOptions === servicerTypeOptions;
  const isBorrower = typeOptions === borrowerTypeOptions;

  const submitHandler = (formValues, actions) => {
    onSubmit(formValues);
    if (shouldReset) {
      actions.resetForm();
    }
  };

  const fields = useMemo(() => {
    const formData = {
      title: values
        ? 'Edit Bill or Distribution'
        : 'Create Bill or Distribution',
      isTitleHidden: false,
    };

    const isBillActiveOrReview =
      values?.status === 'active' || values?.status === 'review' || !values;

    const allFields = [
      {
        title: 'Number',
        name: 'pixel_bill_number',
        type: 'text',
        readOnly: true,
        isHidden: () => !values,
      },
      {
        title: 'Payee type',
        name: 'payee_type',
        type: 'select',
        defaultValue: 'vendor',
        options: [
          {
            value: 'vendor',
            label: 'Vendor',
          },
          {
            value: 'lender',
            label: 'Lender',
          },
          {
            value: 'servicer',
            label: 'Servicer',
          },
          {
            value: 'borrower',
            label: 'Borrower',
          },
        ],
        required: true,
        readOnly: !!isBillPayed,
      },
      {
        title: 'Borrower',
        name: 'borrower_id',
        type: 'select',
        defaultValue: 'vendor',
        options: borrowers?.map((borrower) => ({
          value: borrower?._borrower?.id,
          label:
            borrower?._borrower?.TIN_type === 'EIN'
              ? borrower?._borrower?.entity_name
              : `${borrower?._borrower?.first_name || ''} ${
                  borrower?._borrower?.last_name || ''
                }`,
        })),
        required: isBorrower,
        readOnly: !!isBillPayed,
        isHidden: () => !isBorrower,
      },
      {
        title: 'Type',
        name: 'type',
        type: 'select',
        options: typeOptions,
        required: true,
        readOnly: !!isBillPayed,
      },
      {
        title: 'Vendor',
        name: 'vendor_id',
        type: 'fetchableSelect',
        fetchOptions: {
          key: ['vendorOptions', loanId, vendorType, propertyId],
          queryFn: () => {
            if (propertyId && vendorType === 'Tax') {
              return PropertyService.getUniquePropertyVendors(propertyId);
            }

            return VendorService.getVendors({
              loanId,
              type: vendorType === 'Distribution' ? 'Lien holder' : vendorType,
            });
          },
          valueKey: vendorType === 'Tax' ? '_vendor.id' : 'id',
          labelKey: vendorType === 'Tax' ? '_vendor.name' : 'name',
          enabled:
            !!vendorType &&
            ((vendorType === 'Tax' && propertyId) ||
              vendorType === 'Insurance' ||
              vendorType === 'HOA' ||
              vendorType === 'Distribution'),
        },
        required: isVendor,
        readOnly: !!isBillPayed,
        isHidden: () => !isVendor || isServicer || isBorrower,
      },
      {
        title: 'Lender',
        name: 'lender_id',
        type: 'fetchableSelect',
        fetchOptions: {
          key: ['loanLenders', loanId],
          queryFn: () => LoanService.getLoanLenders(loanId),
          labelKey: 'name',
          valueKey: 'id',
        },
        required: !isVendor && !isServicer && !isBorrower,
        readOnly: !!isBillPayed,
        isHidden: () => isVendor || isServicer || isBorrower,
      },
      {
        title: 'Servicer',
        name: '_servicer.name',
        type: 'text',
        readOnly: true,
        isHidden: () => !values || !isServicer,
      },
      {
        title: 'Due Date',
        name: 'due_date',
        type: 'date',
        required: true,
        readOnly: !!isBillPayed,
      },
      {
        title: 'Status',
        name: 'status',
        type: isBillActiveOrReview ? 'select' : 'text',
        options: [
          {
            value: 'active',
            label: 'Active',
          },
          {
            value: 'review',
            label: 'Review',
          },
        ],
        format: (vals) => {
          if (vals?.status !== 'active' && vals?.status !== 'review') {
            // Capitalize first letter
            return (
              (vals?.status.charAt(0)?.toUpperCase() ?? '') +
              (vals?.status.slice(1) ?? '')
            );
          }
          return values?.status;
        },
        readOnly: !isBillActiveOrReview,
        required: true,
      },
      {
        title: 'Claim',
        name: 'notpresent',
        type: 'text',
        isHidden: (vals) => !vals?._claim || !vals?._claim?.length,
        readOnly: true,
        link: () => {
          return `/loans/${loanId}/claims/${values?._claim?.[0]?.id}?tab=Claim`;
        },
        format: (vals) => {
          const date = vals?._claim?.date_of_loss || new Date();
          const amount = vals?._claim?.claim_amount || 0;
          return `${moment(date).format('M/D/YY')} - $${formatCurrency(
            amount
          )}`;
        },
      },
    ];

    const currentFields = allFields;

    formData.fields = currentFields;

    return [formData];
  }, [
    isBillPayed,
    typeOptions,
    loanId,
    vendorType,
    isVendor,
    isServicer,
    values,
    isBorrower,
    borrowers,
    propertyId,
  ]);

  const { formik, fields: returnedFields } = useForm({
    fields,
    onSubmit: submitHandler,
    values,
  });

  useEffect(() => {
    if (values?.status === 'paid') {
      setIsBillPayed(true);
    } else {
      setIsBillPayed(false);
    }
  }, [values?.status]);

  // Set vendor type
  useEffect(() => {
    if (formik.values.type && formik.values.payee_type === 'vendor') {
      setVendorType(formik.values.type);
    } else {
      setVendorType('');
    }
  }, [formik.values.payee_type, formik.values.type]);

  // Use effect rest type on payee type change
  useEffect(() => {
    if (!formik.dirty) {
      return;
    }

    formik.setFieldValue('type', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.payee_type]);

  // Reset vendor and lender on type change
  useEffect(() => {
    if (!formik.dirty) {
      return;
    }

    formik.setFieldValue('vendor_id', '');
    formik.setFieldValue('lender_id', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.type]);

  useEffect(() => {
    if (formik.values.payee_type === 'vendor') {
      setTypeOptions(vendorTypeOptions);
    } else if (formik.values.payee_type === 'lender') {
      setTypeOptions(lenderTypeOptions);
    } else if (formik.values.payee_type === 'servicer') {
      setTypeOptions(servicerTypeOptions);
    } else if (formik.values.payee_type === 'borrower') {
      setTypeOptions(borrowerTypeOptions);
      //  formik.setFieldValue('type', values.type || 'Refund');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.payee_type]);

  return {
    formik,
    fields: returnedFields,
    values,
  };
};

export default useNewBillForm;
